import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Money, CreditCard } from "@phosphor-icons/react";
import { fmt, getCurrency } from "@/lib/format";

const quickCash = (total) => {
  const buckets = new Set([
    Math.ceil(total),
    Math.ceil(total / 5) * 5,
    Math.ceil(total / 10) * 10,
    Math.ceil(total / 20) * 20,
    Math.ceil(total / 50) * 50,
    Math.ceil(total / 100) * 100,
  ]);
  return [...buckets].filter((v) => v >= total).sort((a, b) => a - b).slice(0, 4);
};

export default function PaymentDialog({ open, onClose, total, onConfirm }) {
  const [method, setMethod] = useState("cash");
  const [cash, setCash] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setMethod("cash");
      setCash("");
      setSubmitting(false);
    }
  }, [open]);

  const cashNum = parseFloat(cash) || 0;
  const change = +(cashNum - total).toFixed(2);
  const canSubmit = method === "card" || (method === "cash" && cashNum >= total);

  const handleConfirm = async () => {
    if (submitting || !canSubmit) return;
    setSubmitting(true);
    try {
      await onConfirm({ payment_method: method, cash_received: method === "cash" ? cashNum : null });
    } finally {
      // parent closes dialog on success; if it stays open (error), re-enable
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Payment</DialogTitle>
        </DialogHeader>

        <div className="text-center py-4">
          <div className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Amount due</div>
          <div className="font-mono text-5xl font-bold text-boba-matcha mt-1" data-testid="pay-total">{fmt(total)}</div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setMethod("cash")}
            className={`p-4 border-2 rounded-md flex flex-col items-center gap-2 transition-colors ${
              method === "cash"
                ? "border-boba-matcha bg-boba-matcha/5"
                : "border-stone-200 hover:bg-stone-50"
            }`}
            data-testid="pay-method-cash"
          >
            <Money size={28} weight="duotone" />
            <div className="font-semibold text-sm">Cash</div>
          </button>
          <button
            onClick={() => setMethod("card")}
            className={`p-4 border-2 rounded-md flex flex-col items-center gap-2 transition-colors ${
              method === "card"
                ? "border-boba-matcha bg-boba-matcha/5"
                : "border-stone-200 hover:bg-stone-50"
            }`}
            data-testid="pay-method-card"
          >
            <CreditCard size={28} weight="duotone" />
            <div className="font-semibold text-sm">Card</div>
          </button>
        </div>

        {method === "cash" && (
          <div className="space-y-3" data-testid="cash-section">
            <div>
              <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Cash received</label>
              <input
                type="number"
                step="0.01"
                value={cash}
                onChange={(e) => setCash(e.target.value)}
                placeholder="0.00"
                className="w-full mt-1 h-12 px-3 border border-boba-stoneLine rounded-md font-mono text-xl focus:outline-none focus:border-boba-matcha"
                data-testid="cash-input"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {quickCash(total).map((v) => (
                <button
                  key={v}
                  onClick={() => setCash(String(v))}
                  className="px-3 py-1.5 border border-stone-300 rounded-full text-sm hover:bg-stone-50"
                  data-testid={`quick-cash-${v}`}
                >
                  {getCurrency()}{v}
                </button>
              ))}
            </div>
            {cashNum > 0 && (
              <div className="flex justify-between bg-boba-muted rounded-md p-3 font-mono">
                <span className="text-boba-inkSoft text-sm">Change</span>
                <span className={`text-lg font-semibold ${change >= 0 ? "text-boba-matcha" : "text-boba-danger"}`} data-testid="change-display">
                  {fmt(change)}
                </span>
              </div>
            )}
          </div>
        )}

        {method === "card" && (
          <div className="bg-boba-muted rounded-md p-4 text-center text-sm text-boba-inkSoft">
            Tap, insert or swipe card on terminal
            <div className="text-[11px] mt-1 font-mono uppercase tracking-widest">Simulated</div>
          </div>
        )}

        <button
          onClick={handleConfirm}
          disabled={!canSubmit || submitting}
          className="w-full h-14 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md text-lg font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="pay-confirm"
        >
          {submitting ? "Processing…" : "Complete Payment"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
